import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, map } from 'rxjs';
import { AppUser } from './auth.service';

export type { TermRequest, Offer, DynamicOffer, UserSettings } from '@creditpath/shared-types';
import type { TermRequest, Offer, DynamicOffer, UserSettings } from '@creditpath/shared-types';

@Injectable({
  providedIn: 'root'
})
export class DatabaseService {
  private firestore: Firestore = inject(Firestore);
  private functions: Functions = inject(Functions);
  private termRequestsCollectionPath = 'loan-request';

  // Initialize callable functions in injection context
  private submitTermRequestCallable = httpsCallable(this.functions, 'submitTermRequest');
  private getTermRequestsForUserCallable = httpsCallable(this.functions, 'getTermRequestsForUser');
  private getOffersForTermRequestCallable = httpsCallable(this.functions, 'getOffersForTermRequest');
  private getTermRequestByIdCallable = httpsCallable(this.functions, 'getTermRequestById');
  private sendClientDetailsCallable = httpsCallable(this.functions, 'sendClientDetails');
  private getUserSettingsCallable = httpsCallable(this.functions, 'getUserSettings');
  private submitBookOfBusinessRequestCallable = httpsCallable(this.functions, 'submitBookOfBusinessRequest');
  private submitContactFormCallable = httpsCallable(this.functions, 'submitContactForm');
  private submitLenderOfferCallable = httpsCallable(this.functions, 'submitLenderOffer');
  private updateLoanRequestStatusCallable = httpsCallable(this.functions, 'updateLoanRequestStatus');
  private cancelTermRequestCallable = httpsCallable(this.functions, 'cancelTermRequest');
  private acceptOfferCallable = httpsCallable(this.functions, 'acceptOffer');
  private updateOfferCallable = httpsCallable(this.functions, 'updateOffer');
  private updateUserSettingsCallable = httpsCallable(this.functions, 'updateUserSettings');

  constructor() { }

  // Saves a new term request via Cloud Function (with rate limiting and validation)
  saveTermRequest(requestData: Omit<TermRequest, 'submissionTimestamp'> & { submissionTimestamp?: Date }): Observable<string> {
    return from(this.submitTermRequestCallable(requestData)).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.termRequestId;
        } else {
          throw new Error(result.data.message || 'Failed to submit term request');
        }
      })
    );
  }

  /** The server decides scope from the caller's role and stored lending types (SEC-1). */
  getTermRequestsForUser(user: AppUser): Observable<TermRequest[]> {
    return from(this.getTermRequestsForUserCallable({})).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.termRequests as TermRequest[];
        } else {
          throw new Error(result.data.message || 'Failed to get term requests');
        }
      })
    );
  }

  addOfferToTermRequest(termRequestId: string, _termStatus: string, offerData: Omit<Offer, 'id' | 'offerDate' | 'optionOneSelected' | 'optionTwoSelected' | 'optionThreeSelected'>): Observable<string> {
    return from(this.submitLenderOfferCallable({ termRequestId, offerData })).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.offerId;
        } else {
          throw new Error(result.data.message || 'Failed to submit offer');
        }
      })
    );
  }

  updateOffer(termRequestId: string, _lenderId: string, offerData: Partial<Omit<Offer, 'id' | 'lenderId' | 'offerDate' | 'optionOneSelected' | 'optionTwoSelected' | 'optionThreeSelected'>>): Observable<void> {
    return from(this.updateOfferCallable({ termRequestId, offerData })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to update offer');
        }
      })
    );
  }

  getOffersForTermRequest(termRequestId: string, user: AppUser): Observable<Offer[]> {
    return from(this.getOffersForTermRequestCallable({ termRequestId })).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.offers as Offer[];
        } else {
          throw new Error(result.data.message || 'Failed to get offers');
        }
      })
    );
  }

  getTermRequestById(termRequestId: string): Observable<TermRequest | undefined> {
    return from(this.getTermRequestByIdCallable({ termRequestId })).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.termRequest as TermRequest;
        } else {
          // Handle not found case
          if (result.data.message?.includes('not found')) {
            return undefined;
          }
          throw new Error(result.data.message || 'Failed to get term request');
        }
      })
    );
  }

  acceptOffer(lenderId: string, termRequestId: string, optionOneSelected: boolean, optionTwoSelected: boolean, optionThreeSelected: boolean): Observable<void> {
    return from(this.acceptOfferCallable({
      lenderId, termRequestId, optionOneSelected, optionTwoSelected, optionThreeSelected
    })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to accept offer');
        }
      })
    );
  }

  markRequestAsFunded(termRequestId: string, _lenderId: string): Observable<void> {
    return from(this.updateLoanRequestStatusCallable({ termRequestId, newStatus: 'funded' })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to mark as funded');
        }
      })
    );
  }

  markRequestAsNotFunded(termRequestId: string, _lenderId: string): Observable<void> {
    return from(this.updateLoanRequestStatusCallable({ termRequestId, newStatus: 'not_funded' })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to mark as not funded');
        }
      })
    );
  }

  updateTermRequestStatus(termRequestId: string, newStatus: string, _lenderId: string): Observable<void> {
    return from(this.updateLoanRequestStatusCallable({ termRequestId, newStatus })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to update status');
        }
      })
    );
  }

  cancelTermRequest(termRequestId: string): Observable<void> {
    return from(this.cancelTermRequestCallable({ termRequestId })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to cancel term request');
        }
      })
    );
  }

  // Send details to client via email using Cloud Function (with rate limiting and validation)
  sendDetailsToClient(
    termRequest: TermRequest,
    offer: Offer,
    clientEmail: string,
    clientName: string,
    clientPhone?: string
  ): Observable<string> {
    const requestData = {
      termRequestId: termRequest.id,
      offerId: offer.id,
      clientEmail,
      clientName,
      clientPhone
    };
    
    return from(this.sendClientDetailsCallable(requestData)).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.emailId;
        } else {
          throw new Error(result.data.message || 'Failed to send client details');
        }
      })
    );
  }

  // Fetch user settings by user ID via Cloud Function (with rate limiting)
  getUserSettings(userId?: string): Observable<UserSettings | undefined> {
    return from(this.getUserSettingsCallable({ targetUserId: userId })).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.userSettings as UserSettings | undefined;
        } else {
          throw new Error(result.data.message || 'Failed to get user settings');
        }
      })
    );
  }

  updateUserSettings(userId: string, settings: Partial<UserSettings>): Observable<void> {
    return from(this.updateUserSettingsCallable({ targetUserId: userId, settings })).pipe(
      map((result: any) => {
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to update user settings');
        }
      })
    );
  }

  // Get user document by user ID from Firestore
  async getUserDocument(userId: string): Promise<any> {
    const userDocRef = doc(this.firestore, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);
    return userDocSnap.exists() ? userDocSnap.data() : null;
  }

  // Send book of business request via Cloud Function (with rate limiting and validation)
  sendBookOfBusinessNotification(requestData: any): Observable<string> {
    return from(this.submitBookOfBusinessRequestCallable(requestData)).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.emailId;
        } else {
          throw new Error(result.data.message || 'Failed to submit book of business request');
        }
      })
    );
  }

  // Send contact form notification via Cloud Function (with rate limiting and validation)
  sendContactFormNotification(formData: any): Observable<string> {
    return from(this.submitContactFormCallable(formData)).pipe(
      map((result: any) => {
        if (result.data.success) {
          return result.data.emailId;
        } else {
          throw new Error(result.data.message || 'Failed to submit contact form');
        }
      })
    );
  }
}
